// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module usePageView
 * @package @geenius/analytics/react
 * @description Tracks page-view transitions for the React variant. The hook is
 * intended for route-level components that should emit an impression on mount
 * and again when their tracked URL changes.
 */

import { useEffect, useRef } from "react";
import { useAnalytics } from "./useAnalytics";

export interface TanStackRouterLocationLike {
  href?: string;
  pathname?: string;
  searchStr?: string;
  hash?: string;
  publicHref?: string;
}

export interface TanStackRouterNavigationEventLike {
  toLocation?: TanStackRouterLocationLike;
}

export interface TanStackRouterLike {
  state?: {
    location?: TanStackRouterLocationLike;
  };
  subscribe?: (
    eventType: "onResolved" | "onLoad",
    listener: (event: TanStackRouterNavigationEventLike) => void,
  ) => () => void;
}

export type PageViewRouteProperties =
  | Record<string, unknown>
  | ((location: TanStackRouterLocationLike | null) => Record<string, unknown>);

export interface UsePageViewOptions {
  url?: string;
  properties?: PageViewRouteProperties;
  router?: TanStackRouterLike | null;
  routerEvent?: "onResolved" | "onLoad";
  getUrl?: (location: TanStackRouterLocationLike) => string;
}

function resolveRouterUrl(
  locationLike: TanStackRouterLocationLike | null | undefined,
  getUrl?: (location: TanStackRouterLocationLike) => string,
): string | null {
  if (!locationLike) return null;
  const customUrl = getUrl?.(locationLike)?.trim();
  if (customUrl) return customUrl;
  const href = locationLike.publicHref ?? locationLike.href;
  if (href?.trim()) return href;
  const pathname = locationLike.pathname?.trim();
  if (!pathname) return null;
  const search = locationLike.searchStr ?? "";
  const rawHash = locationLike.hash?.trim() ?? "";
  const hash = rawHash
    ? rawHash.startsWith("#")
      ? rawHash
      : `#${rawHash}`
    : "";
  return `${pathname}${search}${hash}`;
}

function resolvePageViewUrl(
  url?: string,
  locationLike?: TanStackRouterLocationLike | null,
  getUrl?: (location: TanStackRouterLocationLike) => string,
): string | null {
  const explicitUrl = url?.trim();
  if (explicitUrl) return explicitUrl;

  const routerUrl = resolveRouterUrl(locationLike, getUrl);
  if (routerUrl) return routerUrl;

  if (typeof location !== "undefined" && location.href.trim()) {
    return location.href;
  }

  return null;
}

function resolvePageViewProperties(
  properties: PageViewRouteProperties | undefined,
  locationLike: TanStackRouterLocationLike | null,
): Record<string, unknown> | undefined {
  if (!properties) return undefined;
  return typeof properties === "function"
    ? properties(locationLike)
    : properties;
}

/**
 * Records a page-view event when the calling component mounts or the supplied
 * URL changes. Re-rendering with the same URL does not emit a duplicate view.
 *
 * @param input Optional explicit URL or router-aware tracking options.
 * @returns Nothing. The hook records page-view side effects only.
 *
 * @example
 * ```tsx
 * function DashboardRoute() {
 *   usePageView('/analytics/dashboard')
 *   return <Dashboard />
 * }
 * ```
 */
export function usePageView(input?: string | UsePageViewOptions): void {
  const { page } = useAnalytics();
  const lastTrackedUrl = useRef<string | null>(null);
  const options = typeof input === "string" ? { url: input } : (input ?? {});

  useEffect(() => {
    const trackLocation = (
      locationLike: TanStackRouterLocationLike | null = null,
    ): void => {
      const nextUrl = resolvePageViewUrl(
        options.url,
        locationLike,
        options.getUrl,
      );
      if (!nextUrl || lastTrackedUrl.current === nextUrl) return;

      lastTrackedUrl.current = nextUrl;
      void page(
        nextUrl,
        resolvePageViewProperties(options.properties, locationLike),
      );
    };

    const initialLocation = options.router?.state?.location ?? null;
    trackLocation(initialLocation);

    const unsubscribe = options.router?.subscribe?.(
      options.routerEvent ?? "onResolved",
      (event) => {
        trackLocation(event.toLocation ?? options.router?.state?.location);
      },
    );

    return () => {
      unsubscribe?.();
    };
  }, [
    page,
    options.url,
    options.getUrl,
    options.properties,
    options.router,
    options.routerEvent,
  ]);
}
